import { useEffect, useState, useRef, useReducer } from 'react'
import { toast } from 'sonner'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft, Loader2, Monitor,
  RefreshCw, Tv, Upload, Plus,
  // Icons used by the v3 chrome.
  MoreHorizontal, Trash2,
  // Header 3-dots menu: relink to a different physical device.
  Link as LinkIcon,
  House, SlidersHorizontal, Smartphone, Activity, Settings, MonitorPlay, Zap,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@meno/ui/components/ui/tabs'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@meno/ui/components/ui/dropdown-menu'
import { getScreen, updateScreen, deleteScreen, getBranches, getMedia, getPlaylists, getScreenLogs, getScreenChannels, clearScreenContent, stopAudioStream, type ScreenChannels } from '@meno/ui/lib/api'
import { publishQuickTarget, pickedItemToTarget, publishAudioPicks, publishErrorMessage } from '@/lib/quickPublish'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useIsMobile } from '@meno/ui/hooks/use-mobile'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@meno/ui/components/ui/sheet'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import TransferScreenDialog from '@/components/TransferScreenDialog'
import { getScreenGroups, updateScreenGroup, createScreenGroup } from '@meno/ui/lib/api'
import DeleteScreenDialog   from '@/components/DeleteScreenDialog'
import RelinkScreenDialog   from '@/components/RelinkScreenDialog'
import DetachScreenDialog   from '@/components/DetachScreenDialog'
import PropertiesColumn from '@/components/ScreenDetails/PropertiesColumn'
import PreviewColumn from '@/components/ScreenDetails/PreviewColumn'
import LicenseFallbackBanner from '@/components/ScreenDetails/LicenseFallbackBanner'
import ReconnectPendingBanner from '@/components/ScreenDetails/ReconnectPendingBanner'
import { screenDisplayStatus } from '@meno/ui/components/shared/ScreenStatusBadge'
import SettingsColumn from '@/components/ScreenDetails/SettingsColumn'
import ManagementColumn from '@/components/ScreenDetails/ManagementColumn'
import ScreenActivityLog from '@/components/ScreenDetails/ScreenActivityLog'
import { DeviceTelemetryPanel } from '@/components/ScreenDetails/DeviceTelemetryPanel'
import { DeviceCommandsPanel } from '@/components/ScreenDetails/DeviceCommandsPanel'
import OverviewColumn from '@/components/ScreenDetails/OverviewColumn'
import {
  editBufferReducer, emptyEditBuffer, hydrateFromScreen,
  type MediaRef, type PlaylistRef,
} from '@/components/ScreenDetails/editBufferReducer'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'

type Branch       = {
  id: number; name: string
  address?: string; city?: string; latitude?: number; longitude?: number
}
type ScreenLog    = { id: number; event_type: string; message?: string; created_at: string }
type Screen = {
  id: number; public_id: string; name: string; status: string; branch_id?: number
  effective_status?: string; disconnect_at?: string | null
  location?: string; ip_address?: string; mac_address?: string
  orientation_type: 'horizontal' | 'vertical'
  rotation_degrees: 0 | 90 | 180 | 270
  resolution?: string
  is_active: boolean; created_at: string; last_seen?: string
  device_kind?: 'android' | 'web'   // defaults to 'android' for legacy rows
  device_model?: string; os_version?: string; app_version?: string
  pairing_code?: string; is_paired?: boolean; has_device?: boolean; screenshot_url?: string; screenshot_taken_at?: string | null
  latitude?: number; longitude?: number
  on_time?: string; off_time?: string; is_screen_live?: boolean
  last_error?: string; last_error_at?: string
  was_offline?: boolean
  // Telemetry fields — same GET /screens/{id} response the screens-list info
  // drawer reads (ScreenInfoDrawer.tsx's FullScreen type), just not
  // previously declared here since this page didn't render them. Widened so
  // the new Device tab (DeviceTelemetryPanel) can read them straight off the
  // already-fetched `screen` state instead of a second fetch.
  app_version_code?: number | null; app_channel?: string | null
  auto_launch_permission?: boolean | null; auto_launch_enabled?: boolean | null; is_home_app?: boolean | null
  timezone?: string | null; device_boot_at?: string | null
  latency_ms?: number | null; last_synced_at?: string | null
  battery_pct?: number | null; battery_charging?: boolean | null
  latency_history?: { t: string; ms: number }[] | null
}

// ScreenPreview, PreviewMediaItem, ProgressBar, SyncPreview, and the wrapper
// PreviewColumn now live in components/ScreenDetails/PreviewColumn.tsx.

export default function ScreenDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  // Deep-link support: /screens/{id}?tab=activity opens straight on a tab
  // (the Activity Center's per-screen delivery rows link here).
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tabAliases: Record<string, string> = { info: 'overview', settings: 'display', management: 'manage' }
  const requestedTab = tabAliases[tabParam ?? ''] ?? tabParam
  const initialTab = ['overview', 'display', 'device', 'activity', 'manage'].includes(requestedTab ?? '')
    ? (requestedTab as string)
    : 'overview'
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  // Always ChevronLeft — a global CSS rule (fluid.css) mirrors it under
  // dir="rtl" already; swapping the component here too double-flips it back
  // to the wrong direction (see WizardNavButton.tsx for the full story).
  const ChevronIcon = ChevronLeft
  // usePageHeader is called further down so it can capture the live state
  // (name edit, isSaving) and render the playlist-style header bar.

  // Server data — read-only after load.
  const [screen,       setScreen]       = useState<Screen | null>(null)
  const [branches,     setBranches]     = useState<Branch[]>([])
  const [mediaFiles,   setMediaFiles]   = useState<MediaRef[]>([])
  const [allPlaylists, setAllPlaylists] = useState<PlaylistRef[]>([])
  const [logs,         setLogs]         = useState<ScreenLog[]>([])

  // Orchestration state — load/save/banner.
  const [loading,            setLoading]            = useState(true)
  const [loadError,          setLoadError]          = useState<string | null>(null)
  const [isSaving,           setIsSaving]           = useState(false)

  // Edit buffer — all the unpublished fields. Single reducer so callers
  // dispatch typed actions instead of juggling 17 setState calls. The
  // settings-tab fields, overview-form fields, and assigned-content lists
  // all live here.
  const [buf, dispatch] = useReducer(editBufferReducer, emptyEditBuffer)
  // Unsaved-changes tracking. `savedBufSig` is the buffer signature at the last
  // load / reload / save; `dirty` compares the live buffer to it. syncBaseline
  // snapshots the buffer right after a hydrate (dispatch is async, so we can't
  // read `buf` synchronously). Edits stay buffered until Publish/Save.
  const [savedBufSig, setSavedBufSig] = useState('')
  const syncBaselineRef = useRef(false)
  useEffect(() => {
    if (syncBaselineRef.current) {
      setSavedBufSig(JSON.stringify(buf))
      syncBaselineRef.current = false
    }
  }, [buf])
  // Publish-first channel removal. Removing content / audio only STAGES the
  // clear (buffer-until-publish); the live screen keeps playing until Publish,
  // which commits it. Reload/discard cancels; re-adding to a channel clears its
  // pending flag. Folded into `dirty` so the unsaved-changes guard fires.
  const [pendingContentRemoval, setPendingContentRemoval] = useState(false)
  const [pendingAudioRemoval, setPendingAudioRemoval] = useState(false)
  const dirty = !!screen && (JSON.stringify(buf) !== savedBufSig || pendingContentRemoval || pendingAudioRemoval)
  const { guardOpen, guardNavigate, confirmLeave, cancelLeave } = useUnsavedChangesGuard(dirty)

  // ── v3 chrome state ─────────────────────────────────────────────────────
  // Danger-zone dialogs (Properties column).
  const [deleteOpen,   setDeleteOpen]   = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [relinkOpen,   setRelinkOpen]   = useState(false)
  // Set when Relink was refused (current device offline) and the operator chose
  // to detach instead: after the detach, Relink reopens with this code.
  const [resumeRelinkCode, setResumeRelinkCode] = useState<string | null>(null)
  const [detachOpen,   setDetachOpen]   = useState(false)
  // Asset picker (the "Now Playing" button in Screen Info → Preview Mode).
  const [pickerOpen, setPickerOpen] = useState(false)
  // Audio-stream picker (the audio "Now Playing" row). Opens an audio-only
  // picker; confirming streams the chosen track to this screen. There is no
  // GET for the screen's current audio, so the label reflects the last pick
  // made this session (null = nothing streamed yet from here).
  const [audioPickerOpen, setAudioPickerOpen] = useState(false)
  const [streamedAudio, setStreamedAudio] = useState<{ id: number; name: string } | null>(null)
  // Live channel program (content / audio / stop) resolved server-side — the
  // single source of truth for what each channel card shows (content is now a
  // single-occupant channel written only via publish, not the screen editor).
  const [channels, setChannels] = useState<ScreenChannels | null>(null)
  // Screen groups (real /screen-groups entity). Loaded so the Settings tab's
  // "Assigned group" reflects which group(s) this screen actually belongs to,
  // and so the operator can add/remove it from groups inline.
  const [screenGroups, setScreenGroups] = useState<{ id: number; name: string; screens: { id: number }[] }[]>([])
  const [groupPickerOpen, setGroupPickerOpen] = useState(false)
  // Inline "create a new group" affordance inside the group picker modal.
  const [newGroupName, setNewGroupName] = useState('')
  const [creatingGroup, setCreatingGroup] = useState(false)
  // Preview tab: "playback" cycles items locally; "sync" will compute what
  // the device is currently showing.
  const [previewMode, setPreviewMode] = useState<'playback' | 'sync'>('playback')
  const [activeTab, setActiveTab] = useState(initialTab)
  const isMobile = useIsMobile()
  // Phone: device commands open as a bottom sheet from the header menu.
  const [commandsOpen, setCommandsOpen] = useState(false)
  // The Preview tab exists only on a phone; widening the window while on it
  // would otherwise leave no tab selected.
  useEffect(() => {
    if (!isMobile && activeTab === 'preview') setActiveTab('overview')
  }, [isMobile, activeTab])

  // Refresh the channel program (content / audio / stop). Called on load and
  // after every publish so the channel cards reflect what's actually live.
  const loadChannels = async () => {
    if (!id) return
    try {
      const { data } = await getScreenChannels(id)
      setChannels(data)
    } catch {
      // non-critical — cards fall back to their empty state
    }
  }

  useEffect(() => {
    const load = async () => {
      let fullPlaylists: PlaylistRef[] = []
      try {
        const [branchRes, mediaRes, playlistRes, groupRes] = await Promise.all([
          getBranches(), getMedia(false, 'signage'), getPlaylists(), getScreenGroups(),
        ])
        fullPlaylists = playlistRes.data
        setBranches(branchRes.data)
        setAllPlaylists(playlistRes.data)
        setMediaFiles(mediaRes.data)
        setScreenGroups(groupRes.data)
      } catch {
        // non-critical — continue without sidebar data
      }

      try {
        const [scrRes, logsRes] = await Promise.all([
          getScreen(id!),
          getScreenLogs(id!),
        ])
        const scr: Screen = scrRes.data
        setScreen(scr)
        syncBaselineRef.current = true
        dispatch({ type: 'hydrate', payload: hydrateFromScreen(scr, fullPlaylists) })
        setLogs(logsRes.data ?? [])
        loadChannels()
      } catch (err: any) {
        const status = err?.response?.status
        if (status === 404) {
          setLoadError('Screen not found.')
        } else {
          setLoadError('Failed to load screen. Please check the server and try again.')
        }
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  const save = async () => {
    if (!screen) return
    setIsSaving(true)
    try {
      const { data } = await updateScreen(screen.public_id, {
        name:             buf.name,
        orientation_type: buf.orientationType,
        rotation_degrees: buf.rotationDegrees,
        is_active:        buf.isActive,
        branch_id:   buf.branchId ? Number(buf.branchId) : undefined,
        location:    buf.location || undefined,
        // Settings-tab fields: display_settings is partial-merged server-side.
        display_settings: {
          audio_unmuted: buf.audioMode === 'unmute',
          video_muted:   buf.videoMuted,
          volume:        buf.volume,
          scaling:       buf.scaling,
          hide_player_controls: buf.hidePlayerControls,
        },
        offline_alert_minutes: buf.offlineAlertMinutes,
        // Content is no longer assigned from the screen editor — it lives in the
        // content channel and is set via publish (see handlePickerConfirm). This
        // save only persists identity / orientation / display settings.
      })
      // Publish-first: commit any STAGED channel removals now (they never touched
      // the live screen until this Publish). Reload the channel program after.
      const removingContent = pendingContentRemoval
      const removingAudio = pendingAudioRemoval
      if (removingContent) await clearScreenContent(screen.public_id)
      if (removingAudio) {
        await stopAudioStream({ screen_ids: [screen.id] })
        setStreamedAudio(null)
      }
      if (removingContent || removingAudio) {
        setPendingContentRemoval(false)
        setPendingAudioRemoval(false)
        await loadChannels()
      }
      // The buffer is now the saved baseline — clears the dirty state.
      setSavedBufSig(JSON.stringify(buf))
      setScreen(prev => prev ? { ...prev, name: buf.name } : prev)
      if (data?.was_offline) {
        toast.warning('Screen is currently offline — settings saved and will sync when it reconnects.')
      } else {
        toast.success('Settings saved.')
      }
      // Stay on the page after Publish so the operator can verify the change
      // in the preview / settings — the old behavior navigated away, which
      // made it impossible to iterate on a misconfigured screen.
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save.')
    } finally {
      setIsSaving(false)
    }
  }

  // Reload — discards any unpublished local edits by re-fetching the screen
  // and re-hydrating every publishable field from the server snapshot. The
  // header 3-dots menu surfaces this so an operator who's been tweaking
  // settings can bail out cleanly without leaving the page. Playlists are
  // re-hydrated from `allPlaylists` (already loaded) so we don't need a
  // second round-trip.
  const reloadFromServer = async () => {
    if (!screen) return
    try {
      const { data } = await getScreen(screen.public_id)
      const scr: Screen = data
      setScreen(scr)
      syncBaselineRef.current = true
      dispatch({ type: 'hydrate', payload: hydrateFromScreen(scr, allPlaylists) })
      // Discard any staged channel removals too.
      setPendingContentRemoval(false)
      setPendingAudioRemoval(false)
      toast.success(t('screens.reloaded', 'Reverted to last published state.'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('screens.reloadFailed', 'Failed to reload.'))
    }
  }

  // Silent telemetry refresh for the Device tab (screenshot/refresh quick
  // actions) — unlike reloadFromServer above, this must NOT touch the edit
  // buffer or show a "reverted" toast; it only picks up fresh device fields
  // (screenshot_url, battery, latency, …) on the existing screen object.
  const refreshTelemetry = () => {
    if (!screen) return
    getScreen(screen.public_id).then(r => setScreen(r.data)).catch(() => { /* best-effort */ })
  }

  // ── v3 handlers ─────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!screen) return
    try {
      await deleteScreen(screen.public_id)
      toast.success(t('screens.deleted', 'Screen deleted'))
      navigate('/screens')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.deleteFailed', 'Failed to delete.'))
    }
  }

  // The content channel is single-occupant: picking content PUBLISHES it to
  // this screen (overwriting whatever occupied the channel), mirroring the audio
  // card. A single pick publishes that item directly; picking several media/links
  // at once bundles them into a new playlist (see pickedItemToTarget) so every
  // chosen piece plays, not just the first.
  const handlePickerConfirm = async (picked: PickedItem[]) => {
    setPickerOpen(false)
    if (!screen) return
    const target = pickedItemToTarget(picked)
    if (!target) return
    try {
      await publishQuickTarget(target, {
        screen_ids: [screen.id], group_ids: [], start_at: null, end_at: null,
      })
      setPendingContentRemoval(false) // fresh content supersedes a staged removal
      toast.success(t('screens.contentPublished', 'Content published to this screen'))
      await loadChannels()
    } catch (err: any) {
      toast.error(publishErrorMessage(err, t))
    }
  }

  // Audio "Now Playing" pick → stream to this screen. One track streams
  // directly; several picked at once bundle into a new audio playlist and
  // stream that (see publishAudioPicks) — matching the content auto-playlist.
  const handleAudioPick = async (picked: PickedItem[]) => {
    setAudioPickerOpen(false)
    if (!screen) return
    try {
      const res = await publishAudioPicks(picked, { screen_ids: [screen.id], group_ids: [] })
      if (!res) return
      setStreamedAudio({ id: -1, name: res.name })
      setPendingAudioRemoval(false) // fresh audio supersedes a staged removal
      toast.success(t('screens.audioStreamed', 'Audio streaming to this screen'))
      await loadChannels()
    } catch (err: any) {
      toast.error(publishErrorMessage(err, t))
    }
  }

  // Remove-X on the content / audio channel cards. Publish-first: this only
  // STAGES the clear — the live screen keeps playing until Publish commits it
  // (see `save`). Clicking the X again while staged cancels the removal.
  const handleRemoveContent = () => {
    if (!screen) return
    const next = !pendingContentRemoval
    setPendingContentRemoval(next)
    toast.info(next
      ? t('screens.stagedRemoval', 'Will be removed when you publish.')
      : t('screens.removalCancelled', 'Removal cancelled.'))
  }
  const handleRemoveAudio = () => {
    if (!screen) return
    const next = !pendingAudioRemoval
    setPendingAudioRemoval(next)
    toast.info(next
      ? t('screens.stagedRemoval', 'Will be removed when you publish.')
      : t('screens.removalCancelled', 'Removal cancelled.'))
  }

  // Create a new group (from inside the group picker modal) with this screen
  // already in it, then refresh the list so it appears checked.
  const createGroupWithScreen = async () => {
    const name = newGroupName.trim()
    if (!name || !screen || creatingGroup) return
    setCreatingGroup(true)
    try {
      await createScreenGroup({ name, screen_ids: [screen.id] })
      const r = await getScreenGroups()
      setScreenGroups(r.data)
      setNewGroupName('')
      toast.success(t('screens.groupCreated', 'Group created'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to save.'))
    } finally {
      setCreatingGroup(false)
    }
  }

  // ── v3 header ──────────────────────────────────────────────────────────
  // Built inline (not useMemo) because it depends on many pieces of state
  // and the recompute cost is trivial. Mirrors PlaylistDetailsPage's infoBar.
  const infoBar = screen ? (
    <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => guardNavigate(() => navigate('/screens'))}
          aria-label={t('common.back', 'Back')}
          className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0"
        >
          <ChevronIcon className="w-4 h-4" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
          <Tv className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0 flex flex-col">
          <h1 className="text-[15px] font-semibold text-foreground leading-tight">
            {t('screens.screenDetails', 'Screen details')}
          </h1>
        </div>
      </div>

      <ReconnectPendingBanner
        compact
        screen={screen}
        onReactivated={() => {
          getScreen(screen.public_id).then(r => setScreen(r.data)).catch(() => { /* best-effort */ })
        }}
      />

      <div className="flex items-center gap-2 shrink-0">
        {/* Publish — pushes the current selection to the device. update_screen
            already broadcasts content_update over the WS hub, so the device
            sees the change within a frame of the response. */}
        <Button onClick={save} disabled={isSaving} className="gap-2 h-9">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {t('screens.publish', 'Publish')}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('common.more', 'More')}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {isMobile && (
              <DropdownMenuItem onClick={() => setCommandsOpen(true)} className="gap-2 min-h-10">
                <Zap className="w-4 h-4" />
                {t('screens.deviceCommands', 'Device commands')}
              </DropdownMenuItem>
            )}
            {/* Relink — swap the physical device behind this Screen via the
                new device's pairing code. Lives in the 3-dots menu so it
                sits next to the other lifecycle actions (delete) rather
                than cluttering the Properties Danger Zone. */}
            <DropdownMenuItem onClick={() => setRelinkOpen(true)} className="gap-2">
              <LinkIcon className="w-4 h-4" />
              {t('screens.relink', 'Relink')}
            </DropdownMenuItem>
            {/* Reload — discards unpublished local edits by re-fetching the
                server snapshot. The escape hatch when an operator has been
                tweaking and wants to bail back to the last Published state. */}
            <DropdownMenuItem onClick={reloadFromServer} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              {t('screens.reload', 'Reload')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="gap-2 text-destructive focus:text-destructive">
              <Trash2 className="w-4 h-4" />
              {t('common.delete', 'Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  ) : null

  usePageHeader(
    infoBar ? { render: infoBar } : { title: t('nav.screens', 'Screens') },
  )

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  }

  if (loadError || !screen) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground">
        <Monitor className="w-10 h-10 opacity-30" />
        <p className="text-sm font-medium">{loadError ?? t('screens.notFound', 'Screen not found.')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/screens')}>{t('screens.backToScreens', 'Back to Screens')}</Button>
      </div>
    )
  }

  // Single source of truth for "expired" (screenDisplayStatus, shared with
  // the screens list / dashboard / admin) — surfaced inline on the
  // screenshot button, the channel summary, and the preview frame itself
  // now that the page-level banner (ExpiredScreenBanner) was removed.
  const expired = screenDisplayStatus(screen) === 'expired'

  // One preview, two homes: the right-hand panel on a wide screen, the
  // Preview tab on a phone (stacked under the tabs it only got half the height).
  const previewPanel = (
    <PreviewColumn
      screen={screen}
      orientationType={buf.orientationType}
      setOrientationType={v => dispatch({ type: 'setOrientationType', value: v })}
      rotationDegrees={buf.rotationDegrees}
      setRotationDegrees={v => dispatch({ type: 'setRotationDegrees', value: v })}
      scaling={buf.scaling}
      assignedMedia={buf.assignedMedia}
      assignedMenus={buf.assignedMenus}
      assignedPlaylists={buf.assignedPlaylists}
      mediaDurations={buf.mediaDurations}
      menuDurations={buf.menuDurations}
      previewMode={previewMode}
      setPreviewMode={setPreviewMode}
      onOpenPicker={() => setPickerOpen(true)}
      onOpenAudioPicker={() => setAudioPickerOpen(true)}
      onRemoveContent={handleRemoveContent}
      onRemoveAudio={handleRemoveAudio}
      contentLabel={(() => {
        const name = channels?.content.find(e => e.active)?.content_name ?? null
        if (name == null) return null
        return pendingContentRemoval ? `${name} · ${t('screens.pendingRemoval', 'will be removed on publish')}` : name
      })()}
      audioLabel={(() => {
        const name = channels?.audio.find(e => e.active)?.content_name ?? streamedAudio?.name ?? null
        if (name == null) return null
        return pendingAudioRemoval ? `${name} · ${t('screens.pendingRemoval', 'will be removed on publish')}` : name
      })()}
      expired={expired}
    />
  )

  return (
    // `absolute inset-0` escapes the AppShell's `.p-fluid-l` wrapper so the
    // tabs bar sits flush against the TopBar (the AppShell's content div is
    // `position: relative`, which anchors this fill). The page never scrolls
    // as a whole; instead the Properties / Settings column scrolls
    // internally and the Preview column stays pinned.
    <Tabs value={activeTab} onValueChange={setActiveTab} dir={isRTL ? 'rtl' : 'ltr'} className="absolute inset-0 flex flex-col overflow-hidden">

      {/* Expired-license notice removed from this page per request — the
          fallback-image case (LicenseFallbackBanner) and the
          valid-but-not-reconnected case (ReconnectPendingBanner, with its
          Reactivate action) still show. */}
      <LicenseFallbackBanner screen={screen} />
      {/* ── Body: two sibling panels on the dark canvas. The LEFT panel owns
          the tab switcher (Screen Info / Settings) and its content; the RIGHT
          panel is the Preview, always visible, and expands to fill the row. ── */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] auto-rows-fr gap-3 p-2">
          {/* auto-rows-fr: with no explicit grid-template-rows, the single
              implicit row otherwise sizes to `auto` (its content's
              max-content height) instead of filling the h-full container —
              the two panel cards would end wherever their content stops,
              leaving dead space below on the shorter of the two instead of
              both stretching to the full available height. */}
            {/* Left panel — owns the tab switcher + tab content (scrolls
                internally). forceMount keeps both panels in the React tree so
                switching is free; `data-[state=inactive]:hidden` hides the
                inactive one. */}
            <div className="flex flex-col min-h-0 bg-card rounded-2xl border border-border shadow-sm overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
              {/* Tab switcher lives inside the settings panel */}
              <div className="border-b border-border shrink-0 pt-2 px-[var(--space-m)]">
                <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto bg-transparent p-1 rounded-none">
                  <TabsTrigger
                    value="overview"
                    className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <House className="h-3.5 w-3.5" />{t('screens.overviewTab', 'Overview')}
                  </TabsTrigger>
                  {isMobile && (
                    <TabsTrigger
                      value="preview"
                      className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      <MonitorPlay className="h-3.5 w-3.5" />{t('screens.previewTab', 'Preview')}
                    </TabsTrigger>
                  )}
                  <TabsTrigger
                    value="display"
                    className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />{t('screens.displayTab', 'Display')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="device"
                    className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <Smartphone className="h-3.5 w-3.5" />{t('screens.deviceTab', 'Device')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="activity"
                    className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <Activity className="h-3.5 w-3.5" />{t('screens.activityTab', 'Activity')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="manage"
                    className="h-9 shrink-0 gap-1.5 rounded-lg border border-transparent bg-transparent px-3 text-xs font-medium text-muted-foreground data-[state=active]:border-border data-[state=active]:bg-muted/70 data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <Settings className="h-3.5 w-3.5" />{t('screens.manageTab', 'Manage')}
                  </TabsTrigger>
                </TabsList>
              </div>
              {/* Scrollable tab content */}
              <div className="flex-1 min-h-0 overflow-y-auto p-[var(--space-s)]">
              <TabsContent
                value="overview"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <OverviewColumn
                  screen={{ ...screen, name: buf.name || screen.name }}
                  logs={logs}
                  orientation={buf.orientationType}
                  rotation={buf.rotationDegrees}
                  scaling={buf.scaling}
                  currentGroupName={screenGroups.filter(g => g.screens?.some(s => s.id === screen.id)).map(g => g.name).join(', ') || null}
                  onRename={name => dispatch({ type: 'setName', value: name })}
                  onViewActivity={() => setActiveTab('activity')}
                />
              </TabsContent>

              {isMobile && (
                <TabsContent value="preview" className="mt-0">
                  {previewPanel}
                </TabsContent>
              )}

              <TabsContent
                value="display"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <SettingsColumn
                  screen={screen}
                  orientationType={buf.orientationType}
                  setOrientationType={v => dispatch({ type: 'setOrientationType', value: v })}
                  rotationDegrees={buf.rotationDegrees}
                  setRotationDegrees={v => dispatch({ type: 'setRotationDegrees', value: v })}
                  audioMode={buf.audioMode}
                  setAudioMode={v => dispatch({ type: 'setAudioMode', value: v })}
                  videoMuted={buf.videoMuted}
                  setVideoMuted={v => dispatch({ type: 'setVideoMuted', value: v })}
                  hidePlayerControls={buf.hidePlayerControls}
                  setHidePlayerControls={v => dispatch({ type: 'setHidePlayerControls', value: v })}
                  volume={buf.volume}
                  setVolume={v => dispatch({ type: 'setVolume', value: v })}
                  scaling={buf.scaling}
                  setScaling={v => dispatch({ type: 'setScaling', value: v })}
                  offlineAlertMinutes={buf.offlineAlertMinutes}
                  setOfflineAlertMinutes={v => dispatch({ type: 'setOfflineAlertMinutes', value: v })}
                />
              </TabsContent>

              <TabsContent
                value="device"
                forceMount
                className="mt-0 data-[state=inactive]:hidden space-y-5"
              >
                <DeviceTelemetryPanel screen={screen} logs={logs} />
              </TabsContent>

              <TabsContent
                value="activity"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <div className="space-y-3">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {t('screens.activityTab', 'Activity')}
                  </p>
                  <ScreenActivityLog logs={logs} screenName={screen.name} />
                </div>
              </TabsContent>

              <TabsContent
                value="manage"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <div className="space-y-5">
                  <section>
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('screens.screenAndLocation', 'Screen and location')}</p>
                  <details className="group rounded-xl border border-[hsl(var(--border-control))] bg-card">
                    <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/45"><Tv className="h-4 w-4 text-muted-foreground" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{buf.name || screen.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{screen.location || branches.find(b => b.id === screen.branch_id)?.name || t('screens.locationNotSet', 'Location not set')}</span>
                      </span>
                      <span className="text-muted-foreground transition-transform group-open:rotate-90">›</span>
                    </summary>
                    <div className="border-t border-border p-4">
                      <PropertiesColumn
                        screen={screen}
                        branch={branches.find(b => b.id === screen.branch_id)}
                        isActive={buf.isActive}
                        setIsActive={v => dispatch({ type: 'setIsActive', value: v })}
                        onLocationSaved={refreshTelemetry}
                        locationOnly
                      />
                    </div>
                  </details>
                  </section>
                  <ManagementColumn
                    onDelete={() => setDeleteOpen(true)}
                    onTransfer={() => setTransferOpen(true)}
                    onRelink={() => setRelinkOpen(true)}
                    onDetach={() => setDetachOpen(true)}
                    hasDevice={screen.has_device === true}
                    currentGroupName={screenGroups.filter(g => g.screens?.some(s => s.id === screen.id)).map(g => g.name).join(', ') || null}
                    onManageGroup={() => setGroupPickerOpen(true)}
                    commands={(
                      <DeviceCommandsPanel
                        publicId={screen.public_id}
                        isWeb={(screen.device_kind ?? 'android') === 'web'}
                        autoLaunchEnabled={screen.auto_launch_enabled}
                        onScreenshotTaken={refreshTelemetry}
                        onRefresh={refreshTelemetry}
                        showEditorLinks={false}
                        expired={expired}
                        layout="compact"
                      />
                    )}
                  />
                </div>
              </TabsContent>
              </div>
              {/* Phone: edits are buffered until Publish, and the Publish
                  button is up in the top bar, out of the thumb's reach and
                  easy to forget — so unpublished changes get their own bar. */}
              {isMobile && dirty && (
                <div className="shrink-0 border-t border-border bg-card px-3 py-2 flex items-center justify-between gap-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('screens.unpublishedChanges', 'Unpublished changes')}
                  </span>
                  <Button onClick={save} disabled={isSaving} className="h-11 gap-2 px-5">
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {t('screens.publish', 'Publish')}
                  </Button>
                </div>
              )}
            </div>

            {/* Right column — Preview. Always visible regardless of tab.
                Scrolls internally: switching to vertical (9:16) orientation
                makes the preview frame taller, and a fixed overflow-hidden
                panel would clip the channel tabs/controls below it right off
                the bottom with no way to reach them — overflow-y-auto keeps
                them reachable instead. */}
            {!isMobile && (
              <div className="flex flex-col min-h-0 overflow-y-auto rounded-2xl border border-border bg-card p-[var(--space-s)] shadow-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                {previewPanel}
              </div>
            )}
      </div>

      {/* Phone: every device command in one sheet, as finger-sized rows.
          Restart still asks for confirmation (DeviceCommandsPanel). */}
      {isMobile && (
        <Sheet open={commandsOpen} onOpenChange={setCommandsOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85dvh] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
            <SheetHeader>
              <SheetTitle>{t('screens.deviceCommands', 'Device commands')}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <DeviceCommandsPanel
                publicId={screen.public_id}
                isWeb={(screen.device_kind ?? 'android') === 'web'}
                autoLaunchEnabled={screen.auto_launch_enabled}
                onScreenshotTaken={refreshTelemetry}
                onRefresh={refreshTelemetry}
                showEditorLinks={false}
                expired={expired}
                layout="rows"
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* ── v3: Asset picker (opens from "Now Playing" in Screen Info) ──
          `removable` opens the picker with the screen's current selection
          pre-checked and togglable, so the operator can both attach new
          items and unassign existing ones from the same modal. The
          "Currently Showing" tab gives a quick view of what's already on
          this screen without scrolling the catalog. */}
      <PlaylistContentPicker
        open={pickerOpen}
        tabs={['all', 'media', 'documents', 'links', 'playlists']}
        confirmLabel={t('screens.picker.publishToScreen', 'Publish to screen')}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
      />

      {/* Audio-stream picker — audio-only library; confirming streams the
          chosen track to this screen (independent of the visual content). */}
      <PlaylistContentPicker
        open={audioPickerOpen}
        audioOnly
        excludeIds={streamedAudio ? [streamedAudio.id] : []}
        confirmLabel={t('audioHub.stream', 'Stream to screen')}
        onClose={() => setAudioPickerOpen(false)}
        onConfirm={handleAudioPick}
      />

      {/* ── v3.1: Two-choice delete (trash vs permanent). Trash is a stub for
          now (DeleteScreenDialog shows "Coming soon"); permanent calls
          handleDelete. Archival remains internal-admin-only. */}
      <DeleteScreenDialog
        open={deleteOpen}
        screenName={screen?.name ?? ''}
        onClose={() => setDeleteOpen(false)}
        onDeletePermanent={handleDelete}
        hasDevice={screen?.has_device === true}
        onDetachInstead={() => setDetachOpen(true)}
      />

      {/* ── v3.1: Transfer between spaces (fully wired). ── */}
      {screen && (
        <TransferScreenDialog
          open={transferOpen}
          screenId={screen.id}
          onClose={() => setTransferOpen(false)}
          onTransferred={() => { /* TODO: refetch space membership when surfaced */ }}
        />
      )}

      {/* ── Relink dialog: code entry → confirmation. On success we refetch
          the screen so the device chip (Player card) reflects the new
          model / kind without a page reload. ── */}
      {screen && (
        <DetachScreenDialog
          open={detachOpen}
          // Cancelling the detach abandons the resume; a successful detach
          // calls this first and then onDetached, which restores the code.
          onClose={() => { setDetachOpen(false); setResumeRelinkCode(null) }}
          publicId={screen.public_id}
          screenName={screen.name}
          onDetached={() => {
            const resumeCode = resumeRelinkCode
            getScreen(screen.public_id)
              .then(r => {
                setScreen(r.data)
                // Came here from Relink's "Detach old device": carry on attaching.
                if (resumeCode) { setResumeRelinkCode(resumeCode); setRelinkOpen(true) }
              })
              .catch(() => { /* best-effort */ })
          }}
        />
      )}

      {screen && (
        <RelinkScreenDialog
          open={relinkOpen}
          hasDevice={screen.has_device === true}
          screenId={screen.public_id}
          initialCode={resumeRelinkCode ?? undefined}
          onDetachInstead={code => {
            setRelinkOpen(false)
            setResumeRelinkCode(code || null)
            setDetachOpen(true)
          }}
          onClose={() => { setRelinkOpen(false); setResumeRelinkCode(null) }}
          onRelinked={() => {
            getScreen(screen.public_id).then(r => setScreen(r.data)).catch(() => { /* best-effort */ })
          }}
        />
      )}

      {/* Screen-group picker — add/remove this screen from the org's real
          /screen-groups. Lightweight self-contained overlay (no Dialog import
          needed) so the Settings tab's "Assigned group" is actually functional
          instead of a placeholder. */}
      {screen && groupPickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setGroupPickerOpen(false)}
        >
          <div
            className="bg-card rounded-xl border border-border w-full max-w-md p-5 space-y-3 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold">{t('screens.manageGroups', 'Screen groups')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('screens.manageGroupsHint', 'Add or remove this screen from your groups.')}
            </p>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {screenGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('screens.noGroupsYet', 'No groups yet — create one below.')}
                </p>
              ) : screenGroups.map(g => {
                const inGroup = g.screens?.some(s => s.id === screen.id)
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={async () => {
                      const nextIds = inGroup
                        ? (g.screens ?? []).filter(s => s.id !== screen.id).map(s => s.id)
                        : [...((g.screens ?? []).map(s => s.id)), screen.id]
                      try {
                        await updateScreenGroup(g.id, { screen_ids: nextIds })
                        const r = await getScreenGroups()
                        setScreenGroups(r.data)
                        toast.success(t('screens.groupUpdated', 'Group updated'))
                      } catch (err: any) {
                        toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to save.'))
                      }
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border text-start transition-colors ${
                      inGroup ? 'border-primary bg-muted' : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <span className="text-sm font-medium truncate">{g.name}</span>
                    <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${inGroup ? 'bg-primary border-primary' : 'border-border'}`} />
                  </button>
                )
              })}
            </div>
            {/* Create a new group inline — adds this screen to it immediately. */}
            <div className="flex items-center gap-2 border-t border-border pt-3">
              <input
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createGroupWithScreen() }}
                placeholder={t('screens.newGroupName', 'New group name…')}
                className="flex-1 min-w-0 h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
              />
              <Button
                size="sm"
                onClick={createGroupWithScreen}
                disabled={!newGroupName.trim() || creatingGroup}
                className="gap-1.5 shrink-0"
              >
                {creatingGroup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('common.create', 'Create')}
              </Button>
            </div>

            <div className="flex justify-end pt-1">
              <Button variant="outline" size="sm" onClick={() => setGroupPickerOpen(false)}>
                {t('common.done', 'Done')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* The legacy Add Page / Add Media / Add Playlist / Quick Upload dialogs
          were removed — content is added through PlaylistContentPicker
          (triggered by "Now Playing" in PreviewColumn). */}

      {/* Unsaved-changes guard — fires when leaving with buffered edits. */}
      <ConfirmDialog
        open={guardOpen}
        title={t('common.unsavedTitle', 'Leave without publishing?')}
        description={t('common.unsavedDesc', "You didn't publish your changes. Your changes will be lost if you leave.")}
        confirmLabel={t('common.leaveDiscard', 'Leave & discard')}
        onConfirm={confirmLeave}
        onClose={cancelLeave}
      />
    </Tabs>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen Info / Settings — the LEFT column of the 2-column layout. The page
// (above) puts these inside `<TabsContent value="info">` / `<TabsContent
// value="settings">`; the RIGHT column (PreviewColumn) lives outside both
// TabsContent blocks so it stays visible regardless of active tab.
// PropertiesColumn now lives in components/ScreenDetails/PropertiesColumn.tsx.
// ─────────────────────────────────────────────────────────────────────────────

// ── Right: Preview Mode column ──────────────────────────────────────────────
// PreviewColumn / ScreenPreview / SyncPreview now live in
// components/ScreenDetails/PreviewColumn.tsx.

// SettingCard / SettingOption now live in
// components/ScreenDetails/settings-primitives.tsx, consumed by SettingsColumn.
